import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { MoldHistory, HISTORY_TYPE_LABELS, HistoryType } from '../../types/history.types';
import { Mold } from '../../types';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

interface HistoryListTableProps {
  histories: MoldHistory[];
  molds?: Mold[];
  onView?: (history: MoldHistory) => void;
  onEdit?: (history: MoldHistory) => void;
  onDelete?: (history: MoldHistory) => void;
}

// 타입별 색상
const getTypeColor = (type: HistoryType): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
  switch (type) {
    case 'inbound':
    case 'outbound':
      return 'primary';
    case 'production':
      return 'info';
    case 'maintenance':
    case 'inspection':
      return 'success';
    case 'repair':
    case 'accident':
      return 'error';
    case 'modification':
      return 'secondary';
    case 'disposal':
      return 'warning';
    default:
      return 'default';
  }
};

const HistoryListTable: React.FC<HistoryListTableProps> = ({
  histories,
  molds = [],
  onView,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 캐비티 정보 포맷팅 함수
  const formatCavityInfo = (history: MoldHistory) => {
    if (!history.selectedCavities || history.selectedCavities.length === 0) return '-';
    
    const mold = molds.find(m => m.id === history.moldId);
    if (!mold || !mold.cavities || mold.cavities.length === 0) {
      // 캐비티 정보 없으면 번호만 표시
      return history.selectedCavities.join(', ');
    }

    // 캐비티 번호: 제품명 형식으로 표시
    return history.selectedCavities
      .map(num => {
        const cavity = mold.cavities?.find(c => c.cavityNumber === num);
        return cavity?.productName 
          ? `${num}: ${cavity.productName}`
          : `${num}`;
      })
      .join(', ');
  };

  if (histories.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          등록된 이력이 없습니다.
        </Typography>
      </Box>
    );
  }

  // 모바일 카드뷰
  const renderCardView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {histories.map((history) => (
        <Card key={history.id}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {format(history.date, 'yyyy-MM-dd', { locale: ko })}
              </Typography>
              <Chip
                label={HISTORY_TYPE_LABELS[history.type]}
                color={getTypeColor(history.type)}
                size="small"
              />
            </Box>

            <Typography variant="body2" fontWeight={600} gutterBottom>
              {history.moldNumber}
            </Typography>
            {history.moldName && (
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {history.moldName}
              </Typography>
            )}

            <Typography variant="body1" sx={{ mt: 1, mb: 0.5 }}>
              {history.title}
            </Typography>
            {history.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 1,
                }}
              >
                {history.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              {history.files && history.files.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachFileIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {history.files.length}개
                  </Typography>
                </Box>
              )}
              {history.cost && (
                <Typography variant="caption" color="text.secondary">
                  비용: ₩{history.cost.toLocaleString()}
                </Typography>
              )}
              {history.vendor && (
                <Typography variant="caption" color="text.secondary">
                  업체: {history.vendor}
                </Typography>
              )}
              {history.selectedCavities && history.selectedCavities.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                  패밀리: {formatCavityInfo(history)}
                </Typography>
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
            {onView && (
              <IconButton size="small" onClick={() => onView(history)} sx={{ color: 'white' }}>
                <ViewIcon fontSize="small" />
              </IconButton>
            )}
            {onEdit && (
              <IconButton size="small" onClick={() => onEdit(history)} sx={{ color: 'white' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton size="small" onClick={() => onDelete(history)} sx={{ color: 'white' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </CardActions>
        </Card>
      ))}
    </Box>
  );

  // 데스크탑 테이블뷰
  const renderTableView = () => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>날짜</TableCell>
            <TableCell>금형번호</TableCell>
            <TableCell>유형</TableCell>
            <TableCell>제목</TableCell>
            <TableCell>첨부</TableCell>
            <TableCell>비용</TableCell>
            <TableCell>업체</TableCell>
            <TableCell>사용 패밀리</TableCell>
            <TableCell align="center">작업</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {histories.map((history) => (
            <TableRow key={history.id} hover>
              <TableCell>
                {format(history.date, 'yyyy-MM-dd', { locale: ko })}
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {history.moldNumber}
                </Typography>
                {history.moldName && (
                  <Typography variant="caption" color="text.secondary">
                    {history.moldName}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={HISTORY_TYPE_LABELS[history.type]}
                  color={getTypeColor(history.type)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{history.title}</Typography>
                {history.description && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {history.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                {history.files && history.files.length > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    <AttachFileIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {history.files.length}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                {history.cost ? (
                  <Typography variant="body2">
                    ₩{history.cost.toLocaleString()}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {history.vendor || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {formatCavityInfo(history)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  {onView && (
                    <Tooltip title="상세보기">
                      <IconButton size="small" onClick={() => onView(history)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onEdit && (
                    <Tooltip title="수정">
                      <IconButton size="small" onClick={() => onEdit(history)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="삭제">
                      <IconButton size="small" onClick={() => onDelete(history)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return isMobile ? renderCardView() : renderTableView();
};

export default HistoryListTable;
